//
//  api.ts
//
//  isana.net,inc. Proprietary and Confidential
//
import axios from "axios";
import { API_KEY, API_URL, MOCK_USED } from "../utils/env";
import { logApiError } from "../utils/logError";
import { auth } from "./firebase";
import { BloodPressure, CsvDownload, FitinsightUser } from "./type";

// 静的APIキー（x-api-key）は二次防御として全リクエストに付与し続ける（単独認証にはしない）。
axios.defaults.headers.common["x-api-key"] = API_KEY;

// E5-4: フロント–API認可をAPIキー方式からFirebase IDトークン方式へ移行する。
// ログイン中（管理者）のリクエストにはIDトークンを Authorization: Bearer で付与する。
// この設定はグローバルaxiosに対して行うため、api.ts / googleHealth / ghaCsv の
// すべてのAPI呼び出しに一括で適用される。
// 未ログイン（被験者向け公開ページ: Fitbit/Google Health連携）ではトークンは付与されず、
// 二次防御の x-api-key のみで送信される（サーバ側でも公開エンドポイントはキーのみ受理）。
axios.interceptors.request.use(async (config) => {
  // authStateReady はテスト用モックには存在しないため、関数のときのみ待機する。
  if (typeof auth.authStateReady === "function") {
    await auth.authStateReady();
  }
  const user = auth.currentUser;
  if (user) {
    try {
      // getIdToken は期限切れ時に自動リフレッシュする。
      const idToken = await user.getIdToken();
      config.headers.set("Authorization", `Bearer ${idToken}`);
    } catch (error) {
      // トークン取得失敗時はヘッダを付けずに継続する
      // （保護対象エンドポイントはサーバ側で401となる）。
      logApiError(error);
    }
  }
  return config;
});

type ApiResult<T> = {
  data: T | undefined;
  status: number;
};

// 管理者ログアウト時にサーバサイドでリフレッシュトークンを失効させる（CASA 2.2.1）。
// 認証は上記interceptorが付与する Authorization: Bearer による（signOut() より前に呼ぶこと）。
// 失効に失敗してもクライアント側のセッション破棄は継続させたいため、例外は投げず成否のみ返す。
// timeout: サーバ無応答時にローカルログアウト（signOut/clearAll/遷移）が最大この時間
// ブロックされるため短めの3秒とする。リクエストは送信済みのためクライアントが待つのを
// やめてもサーバ側の失効は実行され、失われるのは「成功の確認」のみ。
export const postLogout = async (): Promise<boolean> => {
  const path = `${API_URL}/logout`;
  return axios
    .post(path, undefined, { timeout: 3000 })
    .then(() => true)
    .catch((error) => {
      logApiError(error);
      return false;
    });
};

// E3-1（統合版）: 連携解除。研究番号(fitinsightUniqueId) または fitbitId を指定して
// テナントAPIの /tenant/userinfo/unlink を呼ぶ。テナント側で UserInfo を論理無効化し、
// GHA連携なら共通基盤トークンを失効＋物理削除、Fitbit連携なら Fitbitトークンを削除する
// （Fitbit/GHA ユーザーを同一フローで解除）。2xx を成功(true)として返す。
const unlinkUser = async (body: object): Promise<Boolean> => {
  const path = `${API_URL}/tenant/userinfo/unlink`;
  const success = await axios
    .post(path, body, { timeout: 10000 })
    .then((res) => res.status >= 200 && res.status < 300)
    .catch((error) => {
      console.error(
        `POST /tenant/userinfo/unlink failed: status=${error.response?.status}`
      );
      return false;
    });
  return success;
};

export const unlinkUserByFitinsightUniqueId = async (
  id: string
): Promise<Boolean> => unlinkUser({ fitinsightUniqueId: id });

export const unlinkUserByFitbitId = async (id: string): Promise<Boolean> =>
  unlinkUser({ fitbitId: id });

export const checkFitinsightUniqueIdExists = async (
  id: string
): Promise<boolean> => {
  const path = `${API_URL}/check-fitinsight-unique-id`;
  let exists = false;
  await axios
    .post(path, {
      fitinsightUniqueId: id,
    })
    .then((res) => {
      if (res.status >= 200 && res.status < 300) {
        exists = res.data.exists;
      }
    })
    .catch((error) => {
      console.error(error);
    });
  return exists;
};

export const getFitinsightUserList = async (
  page: number,
  limit: number = 100
): Promise<
  ApiResult<
    { users: FitinsightUser[]; total: number; isNext: boolean } | undefined
  >
> => {
  const path = MOCK_USED ? "/mock/user-usages" : `${API_URL}/user-usages`;
  const res = await axios
    .get(path, {
      params: {
        page: page,
        limit: limit,
      },
    })
    .then((res) => {
      if (res.status >= 200 && res.status < 300) {
        return {
          data: {
            users: res.data.userUsages,
            total: res.data.total,
            isNext: res.data.isNext,
          },
          status: res.status,
        };
      } else {
        return {
          data: undefined,
          status: res.status,
        };
      }
    })
    .catch((error) => {
      console.error(error);
      return {
        data: undefined,
        status: 500,
      };
    });
  return res;
};

export const getCsvDownloadList = async (
  page: number,
  limit: number = 30
): Promise<
  ApiResult<
    { files: CsvDownload[]; total: number; isNext: boolean } | undefined
  >
> => {
  const path = MOCK_USED ? "/mock/csv-list" : `${API_URL}/csv-list`;
  console.log(`Fetching CSV download list from: ${path}`);
  const res = await axios
    .get(path, {
      params: {
        page: page,
        limit: limit,
      },
    })
    .then((res) => {
      if (res.status >= 200 && res.status < 300) {
        return {
          data: {
            files: res.data.files,
            total: res.data.total,
            isNext: res.data.isNext,
          },
          status: res.status,
        };
      } else {
        return {
          data: undefined,
          status: res.status,
        };
      }
    })
    .catch((error) => {
      console.error(error);
      return {
        data: undefined,
        status: 500,
      };
    });
  return res;
};

export const downloadCustomCsv = async (fileName: string) => {
  const path = MOCK_USED ? "/mock/csv-download" : `${API_URL}/csv-download`;
  const res = await axios
    .get(path, {
      params: {
        fileName: fileName,
      },
    })
    .then((res) => {
      if (res.status >= 200 && res.status < 300) {
        return {
          data: res.data,
          status: res.status,
        };
      } else {
        return {
          data: undefined,
          status: res.status,
        };
      }
    })
    .catch((error) => {
      console.error(error);
      return {
        data: undefined,
        status: 500,
      };
    });
  return res;
};

export const getExportedCsv = async (
  csvType: string,
  startDate: string,
  endDate: string,
  summaryItemIds?: string
) => {
  const path = MOCK_USED ? "/mock/csv/:category" : `${API_URL}/csv/${csvType}`;
  const res = await axios
    .get(path, {
      params: {
        startDate: startDate,
        endDate: endDate,
        ...(summaryItemIds && { summaryItemIds: summaryItemIds }),
      },
    })
    .then((res) => {
      if (res.status >= 200 && res.status < 300) {
        return {
          data: res.data,
          status: res.status,
        };
      } else {
        return {
          data: undefined,
          status: res.status,
        };
      }
    })
    .catch((error) => {
      console.error(error);
      if (error.response) {
        return {
          data: error.response.data,
          status: error.response.status, 
        };
      }
      return {
        data: undefined,
        status: 500,
      };
    });
  return res;
};

export const postBloodPressure = async (
  bloodPressure: BloodPressure
): Promise<boolean> => {
  const path = MOCK_USED ? `/mock/blood-pressure` : `${API_URL}/blood-pressure`;
  let success = false;
  await axios
    .post(path, bloodPressure)
    .then((res) => {
      if (res.status >= 200 && res.status < 300) {
        success = true;
      }
    })
    .catch((error) => {
      console.error(error);
    });
  return success;
};

export const getBloodPressureList = async (
  startDate: string,
  endDate: string,
  companyId: string,
  fitinsightUniqueId: string
): Promise<{ data: BloodPressure[] | undefined; status: number }> => {
  const path = MOCK_USED
    ? `/mock/blood-pressure`
    : `${API_URL}/blood-pressure/${companyId}/${fitinsightUniqueId}`;
  const res = await axios
    .get(path, {
      params: {
        startDate: startDate,
        endDate: endDate,
      },
    })
    .then((res) => {
      if (res.status >= 200 && res.status < 300) {
        return {
          data: res.data,
          status: res.status,
        };
      } else {
        return {
          data: undefined,
          status: res.status,
        };
      }
    })
    .catch((error) => {
      console.error(error);
      return {
        data: undefined,
        status: 500,
      };
    });
  return res;
};

export const postFitinsightUserList = async (
  users: FitinsightUser[]
): Promise<{
  successUsers: string[];
  failedUsers: string[];
  isSuccessful: boolean | string;
}> => {
  const path = MOCK_USED ? `/mock/user-usages` : `${API_URL}/user-usages`;
  const result = await axios
    .post(path, { userUsages: users })
    .then((res) => {
      if (res.status >= 200 && res.status < 300) {
        return {
          successUsers: res.data.successUsers,
          failedUsers: res.data.failedUsers,
          isSuccessful: true,
        };
      } else {
        return {
          successUsers: [],
          failedUsers: [],
          isSuccessful: false,
        };
      }
    })
    .catch((error) => {
      console.error(error);
      return {
        successUsers: [],
        failedUsers: [],
        isSuccessful: error.response.data.message,
      };
    });
  return result;
};
